import Component from '@ux/component';
/* eslint-disable react/jsx-pascal-case */
import { eids, golf } from './enums';
import { FormattedMessage } from 'react-intl';
import OrderConfirmation from '@ux/order-confirmation';
import Notifications from '@ux/notifications';
import Help from '@ux/icon/help';
import HelpLoader from '@ua/help-loader';
import Link from '@ux/link';
import Logo from '@ux/logo';
import HeaderCart from '@px/header-cart';
import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import AppName from './app-name';
import { User } from './account-tray';
import HamburgerMenu from './hamburger';

export const helpApps = ['help', 'how-to', 'community', 'system-status'];

/**
 * Render Version.
 *
 * @class Version
 * @api public
 */
class Version extends Component {
  render() {
    return <Fragment>{this.props.value}</Fragment>;
  }
}

Version.propTypes = {
  value: PropTypes.string
};

/**
 * Render Application Header Footer.
 *
 * @class NavigationTop
 * @api public
 */
export default class NavigationTop extends Component {
  constructor() {
    super(...arguments);

    this.onHelpClicked = this.onHelpClicked.bind(this);
    this.onHelpLoaderChange = this.onHelpLoaderChange.bind(this);
    this.logoTray = React.createRef();

    this.state = {
      shouldShowHelp: false
    };
  }

  /**
   * Function to respond to help affordance clicked and set state accordingly
   * @param {React.MouseEvent} event The event object from the click
   */
  onHelpClicked(event) {
    const { privateLabelId, sidekickDisabled, inAppHelpNavLink } = this.props;

    if (
      privateLabelId === 1 &&
      !sidekickDisabled &&
      (!inAppHelpNavLink || inAppHelpNavLink !== 'on')
    ) {
      event.preventDefault();
      this.setState({
        shouldShowHelp: true
      });
    }
  }

  /**
   * Function to respond to help onChange event and set state accordingly
   * @param {Object} state The incoming help state object after the onChange event
   * @param {boolean} state.minimized Whether the help container should be minimized
   */
  onHelpLoaderChange({ minimized }) {
    this.setState({
      shouldShowHelp: !minimized
    });
  }

  /**
   *
   * @returns {React.element} notifications component
   */
  renderNotifications() {
    const props = this.props;
    const { privateLabelId, urls } = props;
    const notificationMessages = Object.keys(golf.notifications).reduce(
      (memo, key) => {
        memo[key] = <FormattedMessage id={ golf.notifications[key] } />;
        return memo;
      },
      {}
    );

    return (
      <Notifications
        manifest={ props.manifest }
        sso={ urls.sso }
        market={ props.market }
        shopperId={ props.shopperId }
        privateLabelId={ privateLabelId }
        messages={ notificationMessages }
        tooltip={ props.messages[golf.notifications.headerText] }
        showCount={ false }
        showOpenCaret={ true }
        { ...urls.notifications }
      />
    );
  }

  /**
   * Assesses help variant treatment and assigns appropriate UI.
   * Reseller and mobile are rendered serverside.
   * In all other cases there is no serverside help link
   * so that it renders blank before the treatment is
   * chosen on client side
   * @returns {React.ReactElement} The li for the help link
   */
  // eslint-disable-next-line max-statements
  renderHelp() {
    const props = this.props;

    if (
      props.preset === 'internal-header' ||
      helpApps.indexOf(props.app) > -1
    ) {
      return null;
    }

    const isApiReseller = props.privateLabelType === 3;
    const isReseller = props.privateLabelId !== 1;
    let help;
    let label = <Help width={ 30 } height={ 30 } />;
    let linkTitle = props.messages[golf.inApp];
    let extraCssClass;

    if (!isApiReseller || (props.resellerUrls && props.resellerUrls.help)) {
      if (
        !props.isMobile &&
        !props.isTablet &&
        !isReseller &&
        props.market === 'en-US'
      ) {
        const id = golf.helpCenter;
        label = <FormattedMessage id={ id } />;
        linkTitle = props.messages[id];
        extraCssClass = 'help-text';
      }
      help = (
        <li key='help' className={ this.classNames('nav-item', extraCssClass) }>
          { this.renderHelpLabel({
            label,
            tooltip: linkTitle,
            url: props.helpUrl ? props.helpUrl : props.urls.help.href
          }) }
        </li>
      );
    }

    return help;
  }

  /**
   * Render help label
   * @returns {React.ReactElement} The anchor encompassing the help label (if not icon)
   */
  renderHelpLabel({ label, tooltip, url }) {
    const labelIsIcon = label.type === Help;

    return url ? (
      <Link
        href={ url }
        data-eid={ eids.helpCenter }
        data-tcc-ignore={ true }
        onClick={ this.onHelpClicked }
        className='nav-link nav-link-help'
        title={ labelIsIcon ? tooltip : null }>
        { label }
      </Link>
    ) : null;
  }

  /**
   * Render help, cart, notifications, and account tray. ORDER MATTERS.
   * @returns {React.ReactElement[]} Array of components for each global element
   */
  renderGlobalElements() {
    const props = this.props;
    const loggedIn = props.loggedIn;
    const isInternal = props.preset === 'internal-header';
    const isPass = props.preset === 'pass-header';
    const elements = [];
    const isApiReseller = props.privateLabelType === 3;

    elements.push(this.renderHelp());

    if (!isInternal && !isPass && !isApiReseller) {
      const Cart = props.CartComponent;

      elements.push(
        <li
          key='cart'
          className={ this.classNames(
            props.className,
            props.hidden,
            'nav-item'
          ) }>
          <Cart
            items={ props.items }
            market={ props.market }
            messages={ props.messages }
            hideCheckoutNow={ true }
            showEmpty={ true }
            checkout={ props.urls.checkout.href }
            className='nav-link nav-link-cart'
          />
        </li>
      );
    }

    if (loggedIn && !isPass && !isInternal) {
      elements.push(
        <li key='notifications' className='nav-item'>
          { this.renderNotifications() }
        </li>
      );
    }

    if (!isPass) {
      elements.push(
        <li key='account-tray' className='nav-item'>
          <User { ...props } />
        </li>
      );
    }

    return elements;
  }

  /**
   * Renders left nav elements: logo, home button, app name.
   * @returns {React.element} The div encompassing left nav elements
   */
  renderLeftNav() {
    const props = this.props;
    const {
      privateLabelId,
      isMobile,
      isTablet,
      orderId,
      messages,
      market,
      version
    } = props;
    const isNotDesktop = isMobile || isTablet;
    const logoClasses = this.classNames({ 'logo-text': privateLabelId !== 1 });

    // Needed for some GD Only css tweaks on mobile and phablet.
    const outsideLogoClasses = this.classNames('logo', {
      'brand-logo': privateLabelId === 1,
      'go-logo-anchor': privateLabelId === 1
    });
    const logo = (
      <Logo
        className={ outsideLogoClasses }
        country={ props.countryName }
        href={ props.showLogoTray ? '' : props.urls.home.href }
        market={ market }
        messages={ messages }
        logos={ props.logos }
        privateLabelId={ privateLabelId }
        privateLabelName={ props.privateLabelName }
        showCountryNameOnLogo={ false }
        spanClass={ logoClasses }
        urlArgs={ props.urlArgs }
        isMobile={ isMobile }
        tempLogo={ props.tempLogo }
        urls={ props.urls }
        onClick={
          props.showLogoTray &&
          (e => {
            e.preventDefault();
          })
        }
        data-eid='uxp.hyd.app_header.gd_logo.link.click'
      />
    );

    return (
      <div className='nav-left-wrap'>
        { orderId && <OrderConfirmation orderId={ orderId } /> }
        { isNotDesktop &&
          props.navigation &&
          !!(props.navigation.length) &&
          props.showHamburger && (
          <HamburgerMenu { ...props } items={ props.navigation } />
        ) }
        { props.showLogoTray && (
          <User
            quickLinks={ true }
            trayName={ logo }
            traySpanClassName='logo-tray-name'
            { ...props }
          />
        ) }
        { !props.showLogoTray && logo }
        <div className='nav nav-left'>
          <AppName
            appName={ props.appName }
            app={ props.app }
            messages={ messages }
            market={ market }
            urlArgs={ props.urlArgs }
            urls={ props.urls }
            showWaffleMenu={ props.showWaffleMenu }
            customWaffleLinks={ props.customWaffleLinks }
          />
        </div>
        { version && (
          <div className='version hidden-sm-down'>
            <Version value={ version } />
          </div>
        ) }
      </div>
    );
  }

  // eslint-disable-next-line complexity
  render() {
    const { shouldShowHelp } = this.state;
    const {
      env,
      market,
      isMobile,
      isTablet,
      navigationTop,
      preset,
      privateLabelId,
      shopperId,
      sidekickDisabled,
      urls,
      helpUrl
    } = this.props;

    // safari only stuff :(
    let isThisSafari = false;
    if (typeof navigator !== 'undefined') {
      isThisSafari =
        navigator.userAgent.indexOf('Safari') !== -1 &&
        navigator.userAgent.indexOf('Chrome') === -1;
    }

    const helpHref = helpUrl ? helpUrl : urls && urls.help && urls.help.href;

    return (
      <nav
        className={ this.classNames('nav-top', 'clearfix', {
          'safari-only': isThisSafari
        }) }>
        <div className='container'>
          <div
            className={ this.classNames(
              'd-flex',
              'flex-row',
              this.props.mobileSubNav && 'mobile-sub-nav'
            ) }>
            { this.renderLeftNav() }
            <ul className='nav nav-right'>
              {!isMobile && !isTablet && navigationTop && (
                <li className='nav-item'>
                  <div>
                    <a
                      className='custom-link'
                      href={ navigationTop.href }
                      id={ navigationTop.id }
                      target={ navigationTop.target }
                      onClick={ navigationTop.onClick }
                      data-eid={ navigationTop.eid }>
                      { navigationTop.caption }
                    </a>
                  </div>
                </li>
              )}
              { !isMobile && !isTablet && navigationTop && (
                <li className='custom-links-divider' />
              ) }
              { this.renderGlobalElements() }
            </ul>
          </div>
        </div>
        { privateLabelId === 1 &&
          preset !== 'internal-header' &&
          !sidekickDisabled && (
          <HelpLoader
            activated={ shouldShowHelp }
            env={ env }
            market={ market }
            product={ helpHref
              ? helpHref.split('/').pop()
              : null
            }
            shopperId={ shopperId }
            affordance={ true }
            zIndex={ 2000 }
            forceMinimized={ !shouldShowHelp }
            onChange={ this.onHelpLoaderChange }
          />
        ) }
      </nav>
    );
  }
}

NavigationTop.update = function update(nav, done = () => {}) {
  if (Array.isArray(nav) && nav.length === 1) {
    return this.setState(
      {
        navigationTop: nav[0]
      },
      done
    );
  }
};

NavigationTop.updateHelpUrl = function updateHelpUrl(helpUrl, done = () => {}) {
  if (this.props.resellerUrls && this.props.resellerUrls.help) {
    done();
    return;
  }
  return this.setState(
    {
      helpUrl
    },
    done
  );
};

/**
 * Helper to update cart component.
 *
 * @param {React.ReactElement} component Updated React component.
 * @param {Function} done Continuation to call when complete.
 * @returns {state} state change
 * @api public
 */
NavigationTop.updateCartComponent = function updateCartComponent(
  component,
  done = () => {}
) {
  return this.setState(
    {
      CartComponent: component
    },
    done
  );
};

/**
 * Helper to update waffle menu links for internal-header.
 *
 * @param {React.ReactElement} component Updated React component.
 * @param {Function} done Continuation to call when complete.
 * @returns {state} state change
 * @api public
 */

NavigationTop.updateWaffleLinks = function updateWaffleLinks(customWaffleLinks, done = () => {}) {
  const { preset } = this.props;
  if (preset === 'internal-header' && customWaffleLinks && (Array.isArray(customWaffleLinks.topLinks) || Array.isArray(customWaffleLinks.quickLinks))) {
    return this.setState(
      {
        customWaffleLinks: customWaffleLinks
      },
      done
    );
  }
};

/**
 * Default properties for the top half of the navigation.
 *
 * @type {Object}
 * @api public
 */
NavigationTop.defaultProps = {
  app: 'domainmanager',
  CartComponent: HeaderCart,
  features: {},
  preset: 'application-header',
  loggedIn: false,
  manifest: 'applicationheader',
  market: 'en-US',
  privateLabelId: 1,
  privateLabelName: 'GoDaddy.com',
  proshopper: false,
  urlArgs: {},
  sso: {
    app: 'www',
    path: ''
  },
  showHamburger: false,
  urls: {
    notifications: {
      api: {
        href: 'https://mya.dev-godaddy.com/webapi/notifications'
      },
      cache: {
        href: '//img1.wsimg-com.ide/mya/notifications/cache.html'
      },
      endpoint: {
        href: 'https://notifications.api.cloud.dev-godaddy.com/v1/notifications'
      }
    },
    help: {
      href: 'https://www.godaddy.com/help'
    },
    home: {
      href: 'https://www.godaddy.com'
    },
    contactUs: {
      href: 'https://www.godaddy.com/contact-us.aspx'
    },
    checkout: {
      href: 'https://cart.godaddy.com'
    },
    renewals: {
      href: 'https://account.godaddy.com/billing?filter=expires&subFilter=90'
    },
    account: {
      href: 'https://account.godaddy.com'
    },
    products: {
      href: 'https://account.godaddy.com/products'
    },
    pro: {
      href: 'https://pro.godaddy.com/launch/managewp?landingScreen=pro-home'
    },
    proHomeApp: {
      href: 'https://pro.godaddy.com/launch/managewp?landingScreen=pro-home'
    }
  }
};

/**
 * Require properties.
 *
 * @type {Object}
 * @api public
 */
NavigationTop.propTypes = {
  account: PropTypes.string,
  app: PropTypes.string.isRequired,
  appName: PropTypes.string.isRequired,
  className: PropTypes.string,
  CartComponent: PropTypes.elementType,
  countryName: PropTypes.string,
  displayName: PropTypes.string,
  env: PropTypes.string,
  features: PropTypes.object,
  help: PropTypes.string,
  helpUrl: PropTypes.string,
  hidden: PropTypes.string,
  preset: PropTypes.string,
  inAppHelpNavLink: PropTypes.string,
  isMobile: PropTypes.bool,
  isTablet: PropTypes.bool,
  items: PropTypes.number,
  loggedIn: PropTypes.bool,
  logos: PropTypes.object,
  manifest: PropTypes.string,
  market: PropTypes.string.isRequired,
  messages: PropTypes.object.isRequired,
  mobileSubNav: PropTypes.bool,
  name: PropTypes.string,
  navigation: PropTypes.array,
  navigationTop: PropTypes.object,
  orderId: PropTypes.string,
  privateLabelId: PropTypes.number,
  privateLabelType: PropTypes.number,
  privateLabelName: PropTypes.string,
  products: PropTypes.string,
  proshopper: PropTypes.bool,
  shopperId: PropTypes.string,
  showHamburger: PropTypes.bool,
  showWaffleMenu: PropTypes.bool,
  showLogoTray: PropTypes.bool,
  sidekickDisabled: PropTypes.bool,
  renewals: PropTypes.string,
  resellerUrls: PropTypes.shape({
    help: PropTypes.object
  }),
  sso: PropTypes.object,
  tempLogo: PropTypes.string,
  urlArgs: PropTypes.object,
  urls: PropTypes.object.isRequired,
  version: PropTypes.string,
  vip: PropTypes.object,
  customWaffleLinks: PropTypes.object
};

export { Version };
